import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Dialog as DialogType, DialogSection, DialogNode, fetchDialog } from '@/lib/stores/dialogStore';
import { Node, Edge } from '@xyflow/react';
import { fetchLogicFlow } from '@/lib/stores/logicFlowStore';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { UnifiedWizardHeader } from '@/components/wizards/shared/UnifiedWizardHeader';
import { DialogBasicInfoForm } from './DialogBasicInfoForm';
import { DialogSectionsList } from './DialogSectionsList';
import { DialogPlayView } from './DialogPlayView';
import { DialogWriteView } from './DialogWriteView';
import { DialogVariablesPanel } from './DialogVariablesPanel';
import { DialogVariableDeclarations } from './DialogVariableDeclarations';
import { fetchStringsItems, getCachedStringsItems, updateStringSourceTextLocally } from '@/lib/stores/stringsStore';
import { dialogFlowConfig } from '@/components/flow/configs/dialogFlowConfig';
import { useItemLock } from '@/hooks/useItemLock';
import { useItemUpdate } from '@/hooks/useItemUpdate';
import { LockStatusBanner } from '@/components/ui/LockStatusBanner';
import { UpdateNotificationBanner } from '@/components/ui/UpdateNotificationBanner';
import { useDialogSectionOptional } from '@/contexts/DialogSectionContext';
import { DialogNodeProvider, useDialogNodeContextOptional } from '@/contexts/DialogNodeContext';
import { useWizardDraft, DialogDraftData } from '@/hooks/useWizardDraft';

// Local imports
import { DialogWizardProps, DialogData, ViewMode } from './types';
import { useDialogWizardState } from './hooks/useDialogWizardState';
import { useDialogSectionHandlers } from './hooks/useDialogSectionHandlers';
import { useDialogFlowHandlers } from './hooks/useDialogFlowHandlers';
import { useDialogSubmit } from './hooks/useDialogSubmit';
import { DialogFlowView } from './components/DialogFlowView';
import { DialogViewModeSelector } from './components/DialogViewModeSelector';
import { ensureSectionIds, migrateTextToDialog, migrateEndTextToEndDialog, convertNodesToSections, migrateDialogSectionsToNodes, generateSectionIdentifier, deduplicateSectionIdentifiers, migrateEndTextNodes } from './utils/dialogUtils';

// Re-export types for backwards compatibility
export type { DialogData } from './types';

export function DialogWizard({
  isOpen = true,
  onClose,
  onSave,
  onUpdate,
  initialData,
  gameId,
  inline = false,
  onUnsavedChangesChange,
}: DialogWizardProps) {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const dialogSectionContext = useDialogSectionOptional();
  const dialogNodeContext = useDialogNodeContextOptional();

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const state = useDialogWizardState({
    initialData,
    gameId,
  });

  // ============================================================================
  // REFS FOR CROSS-EFFECT COMMUNICATION
  // ============================================================================

  const flowPositionsSnapshotRef = useRef<string>('');
  const flowEdgesSnapshotRef = useRef<string>('');
  const hasRestoredFromDraftRef = useRef(false);
  const skipDraftSaveRef = useRef(false);
  const savedStateSnapshotRef = useRef<any>(null); // Stores the last successfully saved state for comparison
  const deletedStringIdsRef = useRef<Set<string>>(new Set()); // Tracks string IDs that need to be deprecated

  // ============================================================================
  // DRAFT PERSISTENCE
  // ============================================================================

  const isNewDialog = !initialData?.id;
  const { clearDraft, saveDraftData, draftData, hasDraftData } = useWizardDraft<DialogDraftData>({
    draftType: 'dialog',
    gameId: state.effectiveGameId,
    itemId: initialData?.id,
    enabled: true, // Enable for both new and existing dialogs
  });

  // ============================================================================
  // SYNC WITH DIALOG SECTION CONTEXT
  // ============================================================================

  // Sync currentSectionIndex with context
  useEffect(() => {
    if (dialogSectionContext) {
      dialogSectionContext.setCurrentSectionIndex(state.currentSectionIndex);
    }
  }, [state.currentSectionIndex, dialogSectionContext]);

  // Sync viewMode with context (for enabling/disabling node-info tab)
  useEffect(() => {
    if (dialogSectionContext) {
      dialogSectionContext.setViewMode(state.viewMode);
    }
  }, [state.viewMode, dialogSectionContext]);

  // Clear selected node when switching away from flow mode
  useEffect(() => {
    if (dialogNodeContext && state.viewMode !== 'flow') {
      dialogNodeContext.clearSelectedNode();
    }
  }, [state.viewMode, dialogNodeContext]);

  // Sync current section data to context (for real-time display in sidebar)
  useEffect(() => {
    if (dialogSectionContext) {
      const currentViewMode = state.viewMode;
      const currentFlowInitialized = state.flowInitialized;

      // Get the appropriate sections based on current view mode
      const activeSections = (currentViewMode === 'flow' && currentFlowInitialized)
        ? state.localFlowSections
        : state.sections;

      const currentSection = activeSections[state.currentSectionIndex] || null;
      dialogSectionContext.setCurrentSection(currentSection);
    }
  }, [
    dialogSectionContext,
    state.currentSectionIndex,
    state.sections,
    state.localFlowSections,
    state.viewMode,
    state.flowInitialized
  ]);

  // Sync dialog metadata to context (for sidebar display, especially for new unsaved dialogs)
  useEffect(() => {
    if (dialogSectionContext) {
      const currentViewMode = state.viewMode;
      const currentFlowInitialized = state.flowInitialized;

      // Get the appropriate sections based on current view mode
      const activeSections = (currentViewMode === 'flow' && currentFlowInitialized)
        ? state.localFlowSections
        : state.sections;

      dialogSectionContext.setDialogMetadata({
        dialogId: initialData?.id,
        dialogName: state.dialogName,
        dialogKey: state.dialogKey,
        description: state.description,
        tags: state.tags,
        status: state.status,
        owner: state.owner,
        editPermissions: state.editPermissions,
        startingSection: state.startingSection,
        sections: activeSections,
        translatable: state.translatable,
        speakerMode: state.speakerMode,
        speakerTypeId: state.speakerTypeId,
        speakerTemplateIds: state.speakerTemplateIds,
        dialogVariables: state.dialogVariables,
      });
    }
  }, [
    dialogSectionContext,
    initialData?.id,
    state.dialogName,
    state.dialogKey,
    state.description,
    state.tags,
    state.status,
    state.owner,
    state.editPermissions,
    state.startingSection,
    state.sections,
    state.localFlowSections,
    state.viewMode,
    state.flowInitialized,
    state.translatable,
    state.speakerMode,
    state.speakerTypeId,
    state.speakerTemplateIds,
    state.dialogVariables,
  ]);

  // Set up the update callback for sidebar to use (section updates only - not save handler)
  useEffect(() => {
    if (dialogSectionContext) {
      dialogSectionContext.setOnSectionUpdate((sectionIndex: number, updates: any) => {
        // When sidebar updates a section, sync it to the current mode
        // Use refs to ensure we have the latest state
        const currentViewMode = state.viewMode;
        const currentFlowInitialized = state.flowInitialized;

        // Keep local stringsStore in sync so edit mode sees the latest text.
        // Compare old fields with updated fields to find text changes.
        const oldSection = currentViewMode === 'flow' && currentFlowInitialized
          ? state.localFlowSectionsRef.current[sectionIndex]
          : state.sectionsRef.current[sectionIndex];
        if (oldSection?.fields && updates.fields) {
          for (const updatedField of updates.fields) {
            const oldField = oldSection.fields.find((f: any) => f.id === updatedField.id);
            if (oldField?.string_id && updatedField.text !== undefined && oldField.text !== updatedField.text) {
              updateStringSourceTextLocally(oldField.string_id, updatedField.text);
            }
          }
        }

        if (currentViewMode === 'flow' && currentFlowInitialized) {
          // Update localFlowSections
          const currentLocalFlowSections = state.localFlowSectionsRef.current;
          const newSections = currentLocalFlowSections.map((s, idx) =>
            idx === sectionIndex ? { ...s, ...updates } : s
          );
          state.setLocalFlowSections(newSections);
          state.localFlowSectionsRef.current = newSections;

          // Trigger flow regeneration
          if (state.regenerateFlowFromSectionsRef.current) {
            state.regenerateFlowFromSectionsRef.current();
          }
        } else {
          // Update regular sections
          const currentSections = state.sectionsRef.current;
          const newSections = currentSections.map((s, idx) =>
            idx === sectionIndex ? { ...s, ...updates } : s
          );
          state.setSections(newSections);
          state.sectionsRef.current = newSections;
        }

        // Mark as having unsaved changes
        state.setHasUnsavedChanges(true);
      });
    }
  }, [dialogSectionContext, state]);

  // ============================================================================
  // ITEM LOCK SYSTEM
  // ============================================================================

  // Determine if we're in edit mode (existing dialog)
  const isEditMode = !!initialData?.id;

  // Item lock system for collaborative editing
  const {
    lockStatus,
    canEdit: canEditByLock,
    isAcquiring: isAcquiringLock,
    lockError,
    isLockedByOther,
    refreshLockStatus,
    forceLock,
  } = useItemLock({
    itemId: initialData?.id,
    itemType: 'dialog',
    gameId: state.effectiveGameId,
    autoAcquire: true,
    isEditMode,
    onLockLost: () => {
      toast({
        title: 'Edit lock lost',
        description: 'Another user has taken over editing. Your changes may not be saved.',
        variant: 'destructive',
      });
    },
  });

  // Combined edit permission check
  const canEdit = canEditByLock;

  // ============================================================================
  // ITEM UPDATE DETECTION
  // ============================================================================

  // Item update detection for collaborative editing
  const {
    hasPendingUpdate,
    updatedByName,
    updatedByAvatar,
    updatedAt,
    clearPendingUpdate,
  } = useItemUpdate({
    itemId: initialData?.id,
    itemType: 'dialog',
    gameId: state.effectiveGameId,
    enabled: true,
    onItemUpdated: (updatedBy, action) => {
      // Show toast notification when item is updated by another user
      toast({
        title: 'Data updated',
        description: `${updatedBy} updated this dialog. Refresh to see changes.`,
      });
    },
  });

  // ============================================================================
  // SECTION HANDLERS
  // ============================================================================

  const sectionHandlers = useDialogSectionHandlers({
    sections: state.sections,
    setSections: state.setSections,
    toast,
  });

  // ============================================================================
  // FLOW HANDLERS
  // ============================================================================

  const flowHandlers = useDialogFlowHandlers({
    effectiveGameId: state.effectiveGameId,
    initialData,
    dialogName: state.dialogName,
    dialogKey: state.dialogKey,
    description: state.description,
    translatable: state.translatable,
    speakerMode: state.speakerMode,
    speakerTypeId: state.speakerTypeId,
    speakerTemplateIds: state.speakerTemplateIds,
    dialogVariables: state.dialogVariables,
    viewMode: state.viewMode,
    flowNodes: state.flowNodes,
    flowEdges: state.flowEdges,
    localFlowSections: state.localFlowSections,
    flowInitialized: state.flowInitialized,
    contextMenu: state.contextMenu,
    pendingJumpConnection: state.pendingJumpConnection,
    setFlowNodes: state.setFlowNodes,
    setFlowEdges: state.setFlowEdges,
    setLocalFlowSections: state.setLocalFlowSections,
    setHasUnsavedChanges: state.setHasUnsavedChanges,
    setContextMenu: state.setContextMenu,
    setDialogJumpSelectorOpen: state.setDialogJumpSelectorOpen,
    setPendingJumpConnection: state.setPendingJumpConnection,
    sectionsRef: state.sectionsRef,
    localFlowSectionsRef: state.localFlowSectionsRef,
    flowNodesRef: state.flowNodesRef,
    regenerateFlowFromSectionsRef: state.regenerateFlowFromSectionsRef,
    deletedStringIdsRef,
    toast,
  });

  // ============================================================================
  // SUBMIT HANDLERS
  // ============================================================================

  const submitHandlers = useDialogSubmit({
    dialogName: state.dialogName,
    dialogKey: state.dialogKey,
    description: state.description,
    translatable: state.translatable,
    startingSection: state.startingSection,
    speakerMode: state.speakerMode,
    speakerTypeId: state.speakerTypeId,
    speakerTemplateIds: state.speakerTemplateIds,
    dialogVariables: state.dialogVariables,
    tags: state.tags,
    status: state.status,
    owner: state.owner,
    editPermissions: state.editPermissions,
    viewMode: state.viewMode,
    flowInitialized: state.flowInitialized,
    flowId: state.flowId,
    flowNodes: state.flowNodes,
    flowEdges: state.flowEdges,
    needsFlowCreation: state.needsFlowCreation,
    effectiveGameId: state.effectiveGameId,
    initialData,
    inline,
    sectionsRef: state.sectionsRef,
    localFlowSectionsRef: state.localFlowSectionsRef,
    regenerateFlowFromSectionsRef: state.regenerateFlowFromSectionsRef,
    skipDraftSaveRef,
    savedStateSnapshotRef,
    deletedStringIdsRef,
    clearDraft,
    setDialogName: state.setDialogName,
    setDialogKey: state.setDialogKey,
    setDescription: state.setDescription,
    setTranslatable: state.setTranslatable,
    setStartingSection: state.setStartingSection,
    setSpeakerMode: state.setSpeakerMode,
    setSpeakerTypeId: state.setSpeakerTypeId,
    setSpeakerTemplateIds: state.setSpeakerTemplateIds,
    setSections: state.setSections,
    setLocalFlowSections: state.setLocalFlowSections,
    setFlowNodes: state.setFlowNodes,
    setFlowEdges: state.setFlowEdges,
    setFlowId: state.setFlowId,
    setFlowInitialized: state.setFlowInitialized,
    setNeedsFlowCreation: state.setNeedsFlowCreation,
    setIsSubmitting: state.setIsSubmitting,
    setHasUnsavedChanges: state.setHasUnsavedChanges,
    onSave,
    onUpdate,
    onClose,
    toast,
  });

  // ============================================================================
  // NODE UPDATE HANDLER (for node-info sidebar)
  // ============================================================================

  const handleNodeUpdate = useCallback((nodeId: string, updates: Partial<DialogNode>) => {
    
    // Determine which sections to update based on current view mode
    const sectionsToUpdate = (state.viewMode === 'flow' && state.flowInitialized) 
      ? state.localFlowSections 
      : state.sections;

    // Find the section (node) by index
    const nodeIndex = dialogNodeContext?.selectedNodeIndex;
    if (nodeIndex === null || nodeIndex === undefined || nodeIndex < 0) {
      console.warn('[DialogWizard] No valid node index for update');
      return;
    }

    const sectionToUpdate = sectionsToUpdate[nodeIndex];
    if (!sectionToUpdate) {
      console.warn('[DialogWizard] Section not found at index', nodeIndex);
      return;
    }

    // Update the section (which represents a node in the new structure)
    // For compatibility, update both the section and its first field
    const updatedSections = sectionsToUpdate.map((section, idx) => {
      if (idx !== nodeIndex) return section;

      const updatedField = section.fields[0] ? {
        ...section.fields[0],
        ...updates,
        // Preserve required field properties
        id: section.fields[0].id,
        section_id: section.fields[0].section_id,
      } : section.fields[0];

      return {
        ...section,
        ...updates,
        // Update section name if node name changed
        name: updates.name !== undefined ? updates.name : section.name,
        // Update fields
        fields: updatedField ? [updatedField, ...section.fields.slice(1)] : section.fields,
      };
    });

    // Update the appropriate state
    if (state.viewMode === 'flow' && state.flowInitialized) {
      state.setLocalFlowSections(updatedSections);
      state.localFlowSectionsRef.current = updatedSections;

      // Also sync to sections (edit mode state) so changes are immediately visible
      const syncedSections = JSON.parse(JSON.stringify(updatedSections));
      state.setSections(syncedSections);
      state.sectionsRef.current = syncedSections;
      state.setSectionsVersion(v => v + 1);

      // Force flow regeneration to reflect changes
      if (flowHandlers.regenerateFlowFromSections) {
        setTimeout(() => flowHandlers.regenerateFlowFromSections(), 0);
      }
    } else {
      state.setSections(updatedSections);
    }

    // Mark as having unsaved changes
    state.setHasUnsavedChanges(true);

    // Update the selected node data in context
    if (dialogNodeContext && dialogNodeContext.selectedNodeData) {
      dialogNodeContext.setSelectedNode(
        dialogNodeContext.selectedNodeId,
        { ...dialogNodeContext.selectedNodeData, ...updates } as DialogNode,
        nodeIndex
      );
    }
  }, [state, dialogNodeContext, flowHandlers]);

  // Set node update handler in context
  useEffect(() => {
    if (dialogNodeContext) {
      dialogNodeContext.setOnNodeUpdate(handleNodeUpdate);
    }
  }, [dialogNodeContext, handleNodeUpdate]);

  // Set available nodes and dialogs in context
  useEffect(() => {
    if (dialogNodeContext) {
      // Get node identifiers from sections
      const nodes = state.sections.map(section => ({
        node_identifier: section.section_identifier,
        name: section.name
      }));
      dialogNodeContext.setAvailableNodes(nodes);

      // Get all dialogs - we would need to get these from a store or context
      // For now, set empty array
      dialogNodeContext.setAvailableDialogs([]);
    }
  }, [dialogNodeContext, state.sections]);

  // ============================================================================
  // REGISTER SAVE AND METADATA HANDLERS IN CONTEXT
  // ============================================================================

  // Register save handler and metadata handler for sidebar to use
  // Must come AFTER submitHandlers is declared
  useEffect(() => {
    if (dialogSectionContext) {
      // Register save handler for sidebar to trigger
      dialogSectionContext.setOnDialogSave(async () => {
        await submitHandlers.handleSubmit();
      });

      // Register metadata update handler for sidebar
      dialogSectionContext.setOnMetadataUpdate((updates: any) => {
        if (updates.description !== undefined) {
          state.setDescription(updates.description);
        }
        if (updates.tags !== undefined) {
          state.setTags(updates.tags);
        }
        if (updates.status !== undefined) {
          state.setStatus(updates.status);
        }
        if (updates.owner !== undefined) {
          state.setOwner(updates.owner);
        }
        if (updates.edit_permissions !== undefined) {
          state.setEditPermissions(updates.edit_permissions);
        }
        if (updates.startingSection !== undefined) {
          state.setStartingSection(updates.startingSection);
        }
        if (updates.translatable !== undefined) {
          state.setTranslatable(updates.translatable);
        }
        if (updates.speakerMode !== undefined) {
          state.setSpeakerMode(updates.speakerMode);
        }
        if (updates.speakerTypeId !== undefined) {
          state.setSpeakerTypeId(updates.speakerTypeId);
        }
        if (updates.speakerTemplateIds !== undefined) {
          state.setSpeakerTemplateIds(updates.speakerTemplateIds);
        }
        if (updates.dialogVariables !== undefined) {
          state.setDialogVariables(updates.dialogVariables);
        }
        state.setHasUnsavedChanges(true);
      });
    }
  }, [dialogSectionContext, state, submitHandlers]);

  const getFlowPositionsSnapshot = (nodes: Node[]) => {
    return JSON.stringify(
      nodes
        .map((node) => ({ id: node.id, position: node.position }))
        .sort((a, b) => a.id.localeCompare(b.id))
    );
  };

  const getFlowEdgesSnapshot = (edges: Edge[]) => {
    return JSON.stringify(
      edges
        .map((edge) => ({ id: edge.id, source: edge.source, target: edge.target, sourceHandle: edge.sourceHandle, targetHandle: edge.targetHandle }))
        .sort((a, b) => a.id.localeCompare(b.id))
    );
  };

  // ============================================================================
  // VIEW MODE CHANGE HANDLER
  // ============================================================================

  const handleViewModeChange = useCallback(async (value: ViewMode) => {


    // If switching FROM flow mode, sync localFlowSections to sections first
    if (state.viewMode === 'flow' && value !== 'flow' && state.flowInitialized && state.localFlowSectionsRef.current.length > 0) {
      const committedSections = JSON.parse(JSON.stringify(state.localFlowSectionsRef.current));
      state.sectionsRef.current = committedSections;
      state.setSections(committedSections);
      state.setSectionsVersion(v => v + 1);
    }

    // If switching TO flow mode
    if (value === 'flow' && state.effectiveGameId) {
      if (!state.flowId && !state.flowInitialized) {
        // No flow exists, create one locally

        // Generate flow nodes and edges locally (don't save to DB yet)
        const dialogForFlow: DialogType = {
          id: initialData?.id || '',
          game_id: state.effectiveGameId,
          name: state.dialogName,
          dialog_key: state.dialogKey,
          description: state.description,
          tags: initialData?.tags || [],
          folder: initialData?.folder,
          status: (initialData?.status as 'draft' | 'published') || 'draft',
          data: migrateDialogSectionsToNodes({
            sections: state.localFlowSections.length > 0 ? state.localFlowSections : state.sections,
            translatable: state.translatable,
            speakerMode: state.speakerMode,
            speakerTypeId: state.speakerTypeId,
            speakerTemplateIds: state.speakerTemplateIds,
          }),
          created_at: initialData?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        // Generate nodes and edges locally
        const generatedNodes = dialogFlowConfig.dataToNodes(dialogForFlow);
        const generatedEdges = dialogFlowConfig.dataToEdges(dialogForFlow);

        // Add callbacks to node data
        const nodesWithCallbacks = flowHandlers.addCallbackToNodes(generatedNodes);


        state.setFlowNodes(nodesWithCallbacks);
        state.setFlowEdges(generatedEdges);
        state.setNeedsFlowCreation(true);

        // Initialize snapshots for the newly created flow
        // This allows position tracking to work correctly
        flowPositionsSnapshotRef.current = getFlowPositionsSnapshot(nodesWithCallbacks);
        flowEdgesSnapshotRef.current = getFlowEdgesSnapshot(generatedEdges);

        // Set unsaved changes after initializing snapshots
        state.setHasUnsavedChanges(true);

        toast({
          title: 'Flow Mode',
          description: 'Flow created locally. Save to persist the flow visualization.',
        });
      } else {
        // Flow exists, need to sync sections to localFlowSections before regenerating
        // This ensures the flow picks up any changes made in edit mode
        const copiedSections = JSON.parse(JSON.stringify(state.sectionsRef.current));
        state.localFlowSectionsRef.current = copiedSections;
        state.setLocalFlowSections(copiedSections);

        // Now regenerate the flow to pick up the changes
        setTimeout(() => flowHandlers.regenerateFlowFromSections(), 0);
      }
    }

    state.setViewMode(value);
    if (value === 'play') {
      state.setCurrentSectionIndex(0);
    }
  }, [state, initialData, flowHandlers, toast, getFlowPositionsSnapshot, getFlowEdgesSnapshot]);

  // ============================================================================
  // REVERT HANDLER
  // ============================================================================

  const handleRevert = useCallback(async () => {
    if (!initialData) return;


    // Reload all data from initialData
    state.setDialogName(initialData.name || 'New Dialog');
    state.setDialogKey(initialData.dialog_key || '');
    state.setDescription(initialData.description || '');
    state.setTranslatable(initialData.data?.translatable !== false);
    state.setStartingSection(initialData.data?.startingSection);
    state.setSpeakerMode(initialData.data?.speakerMode || 'none');
    state.setSpeakerTypeId(initialData.data?.speakerTypeId || null);
    state.setSpeakerTemplateIds(initialData.data?.speakerTemplateIds || []);
    state.setDialogVariables(initialData.data?.variables || []);
    state.setTags(initialData.tags || []);
    state.setStatus((initialData.status as string) || 'published');
    state.setOwner(initialData.owner || null);
    state.setEditPermissions(initialData.edit_permissions || 'Open');

    // Reload sections - prefer nodes as source of truth
    let sectionsForUI: DialogSection[];

    if (initialData.data?.sections && initialData.data.sections.length > 0) {
      // Nodes exist - use them as source of truth
      sectionsForUI = initialData.data.sections;
    } else {
      // No data at all
      sectionsForUI = [];
    }
    state.setSections(sectionsForUI);
    state.sectionsRef.current = sectionsForUI;

    // If in flow mode, also reload flow sections and flow data
    if (state.viewMode === 'flow' && state.flowInitialized) {
      const copiedSections = JSON.parse(JSON.stringify(sectionsForUI));
      state.setLocalFlowSections(copiedSections);
      state.localFlowSectionsRef.current = copiedSections;

      // If there's a flow ID, reload the saved flow data to restore node positions
      if (initialData.flow && state.effectiveGameId) {
        try {
          const flow = await fetchLogicFlow(initialData.flow, state.effectiveGameId);
          if (flow && flow.data) {
            // Restore the saved flow nodes and edges
            state.setFlowNodes(flow.data.nodes || []);
            state.setFlowEdges(flow.data.edges || []);

            // Update the snapshots to reflect the reverted state
            flowPositionsSnapshotRef.current = getFlowPositionsSnapshot(flow.data.nodes || []);
            flowEdgesSnapshotRef.current = getFlowEdgesSnapshot(flow.data.edges || []);

          }
        } catch (error) {
          console.error('[DialogWizard] Failed to reload flow data:', error);
          // If loading flow fails, regenerate from sections
          if (state.regenerateFlowFromSectionsRef.current) {
            setTimeout(() => state.regenerateFlowFromSectionsRef.current?.(), 0);
          }
        }
      } else {
        // No saved flow, regenerate from sections
        if (state.regenerateFlowFromSectionsRef.current) {
          setTimeout(() => state.regenerateFlowFromSectionsRef.current?.(), 0);
        }
      }
    }

    // Reset unsaved changes flag
    state.setHasUnsavedChanges(false);

    toast({
      title: 'Changes Reverted',
      description: 'All changes have been discarded and the dialog has been restored to its saved state.',
    });
  }, [initialData, state, toast, getFlowPositionsSnapshot, getFlowEdgesSnapshot]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Initialize local flow state when entering flow view, commit when leaving
  useEffect(() => {
    // Wait for data to be loaded before initializing flow
    if (!state.isDataLoaded) return;

    const prevViewMode = state.prevViewModeRef.current;

    // Check if we're leaving flow view
    if (prevViewMode === 'flow' && state.viewMode !== 'flow' && state.flowInitialized && state.localFlowSectionsRef.current.length > 0) {
      const committedSections = JSON.parse(JSON.stringify(state.localFlowSectionsRef.current));

      // CRITICAL FIX: Update ref synchronously along with state to prevent race conditions
      state.sectionsRef.current = committedSections;
      state.setSections(committedSections);
      state.setSectionsVersion(v => v + 1);
      state.setHasUnsavedChanges(true);
    }

    // Check if we're entering flow view
    if (state.viewMode === 'flow') {
      if (!state.flowInitialized) {
        const copiedSections = JSON.parse(JSON.stringify(state.sectionsRef.current));
        // CRITICAL FIX: Update ref synchronously along with state
        state.localFlowSectionsRef.current = copiedSections;
        state.setLocalFlowSections(copiedSections);
        state.setFlowInitialized(true);
      } else if (prevViewMode !== 'flow') {
        const copiedSections = JSON.parse(JSON.stringify(state.sectionsRef.current));
        // CRITICAL FIX: Update ref synchronously along with state
        state.localFlowSectionsRef.current = copiedSections;
        state.setLocalFlowSections(copiedSections);
      }
    }

    state.prevViewModeRef.current = state.viewMode;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.viewMode, state.flowInitialized, state.isDataLoaded]);

  // Real-time sync: When localFlowSections changes in flow mode, immediately update sections for edit mode
  // This ensures that changes in flow mode (like deletions, name changes, etc.) are instantly visible in edit mode
  // CRITICAL: This effect uses a ref-based comparison to avoid circular dependencies
  const lastSyncedSectionsRef = useRef<string>('');

  useEffect(() => {
    if (!state.flowInitialized || state.viewMode !== 'flow') return;

    // Use ref for comparison to avoid circular dependency with sections state
    // Compare full section content to detect all changes (text, type, speaker, options, etc.)
    const currentLocalFlowSectionsKey = JSON.stringify(state.localFlowSections);
    const currentSectionsKey = JSON.stringify(state.sections);

    // Only sync if localFlowSections changed AND is different from sections
    const sectionsChanged = currentLocalFlowSectionsKey !== currentSectionsKey;

    // Prevent infinite loops by tracking the last synced state
    const alreadySynced = lastSyncedSectionsRef.current === currentLocalFlowSectionsKey;

    if (sectionsChanged && !alreadySynced) {
      // Deep clone to prevent reference issues
      const syncedSections = JSON.parse(JSON.stringify(state.localFlowSections));
      state.setSections(syncedSections);
      state.sectionsRef.current = syncedSections;
      state.setSectionsVersion(v => v + 1);

      // Track that we've synced this state
      lastSyncedSectionsRef.current = currentLocalFlowSectionsKey;
    }
  }, [state.localFlowSections, state.flowInitialized, state.viewMode, state.setSections, state.sectionsRef, state.setSectionsVersion]);

  // Regenerate flow from sections when data is first loaded in flow mode
  // This ensures node text matches the latest sections data
  useEffect(() => {
    if (!state.isDataLoaded || !state.flowInitialized || state.viewMode !== 'flow') return;
    if (state.localFlowSectionsRef.current.length === 0) return; // Wait for localFlowSections to be initialized

    // Regenerate flow to sync with sections data
    if (state.regenerateFlowFromSectionsRef.current) {
      state.regenerateFlowFromSectionsRef.current();
    }
  }, [state.isDataLoaded, state.flowInitialized, state.viewMode, state.localFlowSectionsRef, state.regenerateFlowFromSectionsRef]);

  // Set needsFlowCreation flag when flow nodes are generated for a dialog without an existing flow
  useEffect(() => {
    // Only trigger if we're in flow mode and flow is initialized
    if (state.viewMode !== 'flow' || !state.flowInitialized) return;

    // If there's no flowId but we have flow nodes, we need to create a flow on save
    if (!state.flowId && state.flowNodes.length > 0 && !state.needsFlowCreation) {
      state.setNeedsFlowCreation(true);
    }
  }, [state.viewMode, state.flowInitialized, state.flowId, state.flowNodes.length, state.needsFlowCreation, state.setNeedsFlowCreation]);

  // Load initial data if editing
  useEffect(() => {
    const loadDialogData = async () => {
      // Reset data loaded state to prevent flow from initializing with stale data
      state.setIsDataLoaded(false);
      state.setFlowInitialized(false);

      // Initialize saved state snapshot to null on reload
      savedStateSnapshotRef.current = null;

      // Check if we have draft data to restore (from tab switch) - only restore once
      // CRITICAL FIX: Don't restore draft if it has empty sections but initialData has real sections
      // This prevents corrupted/stale draft data from overwriting actual dialog data
      const draftHasEmptySections = !draftData?.sections || draftData.sections.length === 0;
      const initialDataHasSections = initialData?.data?.sections && initialData.data.sections.length > 0;
      const shouldSkipDraftRestore = draftHasEmptySections && initialDataHasSections;

      if (hasDraftData && draftData && !hasRestoredFromDraftRef.current && !shouldSkipDraftRestore) {
        hasRestoredFromDraftRef.current = true;

        // Restore all draft state
        state.setDialogName(draftData.dialogName || 'New Dialog');
        state.setDialogKey(draftData.dialogKey || '');
        state.setDescription(draftData.description || '');
        state.setTranslatable(draftData.translatable !== false);
        state.setSpeakerMode((draftData.speakerMode as any) || 'none');
        state.setSpeakerTypeId(draftData.speakerTypeId || null);
        state.setSpeakerTemplateIds(draftData.speakerTemplateIds || []);
        state.setDialogVariables(draftData.dialogVariables || []);
        state.setSections(draftData.sections || []);

        // Restore flow state
        if (draftData.viewMode) {
          state.setViewMode(draftData.viewMode as any);
        }
        if (draftData.localFlowSections) {
          state.setLocalFlowSections(draftData.localFlowSections);
        }
        if (draftData.flowNodes) {
          state.setFlowNodes(draftData.flowNodes);
        }
        if (draftData.flowEdges) {
          state.setFlowEdges(draftData.flowEdges);
        }
        if (draftData.flowViewport) {
          state.setFlowViewport(draftData.flowViewport);
        }
        if (draftData.flowInitialized) {
          state.setFlowInitialized(true);
        }

        // Keep the flowId from initialData if it exists
        if (initialData?.flow) {
          state.setFlowId(initialData.flow);
        }

        state.setHasUnsavedChanges(true);
        state.setIsDataLoaded(true);
        return; // Skip normal loading, draft has been restored
      }

      // Log if we skipped draft restoration
      if (shouldSkipDraftRestore && hasDraftData && draftData) {
        hasRestoredFromDraftRef.current = true; // Mark as restored to prevent re-running

        // Even though we're skipping full draft restoration, we should still restore view-related state
        // These are independent of section data and should be preserved when switching tabs
        if (draftData.viewMode) {
          state.setViewMode(draftData.viewMode as any);
        }

        // Restore flow-related state if they were in flow mode
        if (draftData.localFlowSections) {
          state.setLocalFlowSections(draftData.localFlowSections);
        }
        if (draftData.flowNodes) {
          state.setFlowNodes(draftData.flowNodes);
        }
        if (draftData.flowEdges) {
          state.setFlowEdges(draftData.flowEdges);
        }
        if (draftData.flowViewport) {
          state.setFlowViewport(draftData.flowViewport);
        }
        if (draftData.flowInitialized) {
          state.setFlowInitialized(true);
        }
      }

      if (initialData) {


        state.setDialogName(initialData.name || 'New Dialog');
        state.setDialogKey(initialData.dialog_key || '');
        state.setDescription(initialData.description || '');
        state.setTranslatable(initialData.data?.translatable !== false);
        state.setStartingSection(initialData.data?.startingSection);
        state.setSpeakerMode(initialData.data?.speakerMode || 'none');
        state.setSpeakerTypeId(initialData.data?.speakerTypeId || null);
        state.setSpeakerTemplateIds(initialData.data?.speakerTemplateIds || []);
        state.setDialogVariables(initialData.data?.variables || []);
        state.setFlowId(initialData.flow || null);
        state.setTags(initialData.tags || []);
        state.setStatus((initialData.status as string) || 'published');
        state.setOwner(initialData.owner || null);
        state.setEditPermissions(initialData.edit_permissions || 'Open');

        // Load sections - prefer nodes as source of truth
        let sectionsWithIds: DialogSection[];

        if (initialData.data?.nodes && initialData.data.nodes.length > 0) {
          // Nodes exist - use them as source of truth
          sectionsWithIds = convertNodesToSections(initialData.data.nodes);
        } else if (initialData.data?.sections && initialData.data.sections.length > 0) {
          // No nodes - migrate from legacy sections
          const migratedData = migrateDialogSectionsToNodes(initialData.data);
          sectionsWithIds = migratedData.sections || [];
        } else {
          // No data at all
          sectionsWithIds = [];
        }

        // If dialog has TMS-linked fields, fetch strings and populate from TMS
        if (state.effectiveGameId && sectionsWithIds.some(s => s.fields?.some(f => f.string_id))) {
          try {
            await fetchStringsItems(state.effectiveGameId);
            const strings = getCachedStringsItems(state.effectiveGameId);

            sectionsWithIds = sectionsWithIds.map(section => ({
              ...section,
              fields: section.fields?.map(field => {
                const fieldText = field.string_id
                  ? (strings.find(s => s.id === field.string_id)?.source_text || field.text)
                  : field.text;

                const updatedOptions = field.options && field.options.length > 0
                  ? field.options.map(option => {
                    if (option.string_id) {
                      const linkedString = strings.find(s => s.id === option.string_id);
                      if (linkedString) {
                        return { ...option, option_text: linkedString.source_text };
                      }
                    }
                    return option;
                  })
                  : field.options;

                return { ...field, text: fieldText, options: updatedOptions };
              }) || [],
            }));
          } catch (error) {
            console.error('Failed to load TMS strings for dialog:', error);
          }
        }

        sectionsWithIds = migrateTextToDialog(sectionsWithIds);
        sectionsWithIds = migrateEndTextToEndDialog(sectionsWithIds);

        // Deduplicate section identifiers (fixes old dialogs with duplicate IDs)
        sectionsWithIds = deduplicateSectionIdentifiers(sectionsWithIds);

        // Apply section-to-node migration if needed
        if (initialData.data) {
          // Migrate end-text nodes to end-dialog in nodes array
          if (initialData.data.nodes) {
            initialData.data.nodes = migrateEndTextNodes(initialData.data.nodes);
          }
          
          const migratedData = migrateDialogSectionsToNodes(initialData.data);
          // Update the initialData reference so flow mode gets the nodes
          if (initialData.data !== migratedData) {
            initialData.data = migratedData as any;
          }
          // For now, we still use sections internally for the edit view
          // But the nodes will be used by flow mode
          if (migratedData.nodes && !sectionsWithIds.length) {
            // If we only have nodes (new structure), we need to keep sections for edit mode
            // This shouldn't happen normally since migration preserves sections
          }
        }

        state.setSections(sectionsWithIds);
        state.setHasUnsavedChanges(false);

        // Initialize localFlowSections if starting in flow mode
        // This ensures edits work correctly even before switching modes
        if (state.viewMode === 'flow') {
          state.setLocalFlowSections(JSON.parse(JSON.stringify(sectionsWithIds)));
        }

        // Load flow data if flow ID exists
        if (initialData.flow && state.effectiveGameId) {
          state.setIsLoadingFlow(true);
          try {
            const flow = await fetchLogicFlow(initialData.flow, state.effectiveGameId);
            if (flow && flow.data) {
              state.setFlowNodes(flow.data.nodes || []);
              state.setFlowEdges(flow.data.edges || []);
              flowPositionsSnapshotRef.current = getFlowPositionsSnapshot(flow.data.nodes || []);
              flowEdgesSnapshotRef.current = getFlowEdgesSnapshot(flow.data.edges || []);

            }
          } catch (error) {
            console.error('Failed to load flow data:', error);
          } finally {
            state.setIsLoadingFlow(false);
          }
        } else if (state.flowNodes.length === 0) {
          // No saved flow - initialize empty snapshots
          flowPositionsSnapshotRef.current = '[]';
          flowEdgesSnapshotRef.current = '[]';
        }

        // Mark data as loaded so flow can initialize
        state.setIsDataLoaded(true);

        // CRITICAL FIX: Initialize saved state snapshot with the loaded data
        // This becomes the baseline for change detection
        savedStateSnapshotRef.current = {
          ...initialData,
          data: {
            ...initialData.data,
            sections: sectionsWithIds,
          },
        };


      } else {
        // Reset form when creating new
        state.setDialogName('New Dialog');
        state.setDialogKey('');
        state.setDescription('');
        state.setTranslatable(true);
        state.setSpeakerMode('none');
        state.setSpeakerTypeId(null);
        state.setSpeakerTemplateIds([]);
        state.setDialogVariables([]);

        const sectionId = `section_${Date.now()}`;
        const fieldId = `field_${Date.now()}_1`;
        state.setSections([{
          id: sectionId,
          name: 'Section 1',
          section_identifier: generateSectionIdentifier(),
          order_index: 0,
          fields: [{
            id: fieldId,
            type: 'dialog',
            text: '',
            translatable: true,
            order_index: 0,
          }],
          fieldOrder: [fieldId],
        }]);
        state.setHasUnsavedChanges(false);

        // Mark data as loaded so flow can initialize
        state.setIsDataLoaded(true);
      }
    };

    loadDialogData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, isOpen, state.effectiveGameId]);

  // Track unsaved changes
  useEffect(() => {
    if (!initialData) return;

    // Skip change detection until initial data loading is complete
    // This prevents false positives during wizard mount/remount where default state
    // values would be compared against initialData before the init effect has run
    if (!state.isDataLoaded) return;

    // When in flow view, check localFlowSections instead of sections
    const sectionsToCheck = state.viewMode === 'flow' && state.flowInitialized
      ? state.localFlowSections
      : state.sections;

    // CRITICAL FIX: Skip change detection during state reload/transition
    // When both sections arrays are empty but initialData has sections, we're in a reload state
    const isReloading = sectionsToCheck.length === 0 &&
      state.sections.length === 0 &&
      state.localFlowSections.length === 0 &&
      (initialData.data?.sections?.length || 0) > 0;

    if (isReloading) {
      return; // Skip change detection during reload
    }

    // Use saved state snapshot if available (updated after successful save)
    // This prevents false positives when initialData prop hasn't been updated yet
    const comparisonBaseline = savedStateSnapshotRef.current || initialData;

    const currentFlowSnapshot = getFlowPositionsSnapshot(state.flowNodes);
    const flowPositionsChanged = flowPositionsSnapshotRef.current !== '' &&
      currentFlowSnapshot !== flowPositionsSnapshotRef.current;

    // Check if edges have changed by comparing snapshots
    const currentEdgesSnapshot = getFlowEdgesSnapshot(state.flowEdges);
    const flowEdgesChanged = flowEdgesSnapshotRef.current !== '' &&
      currentEdgesSnapshot !== flowEdgesSnapshotRef.current;

    const hasChanges =
      state.dialogName !== (comparisonBaseline.name || '') ||
      state.dialogKey !== (comparisonBaseline.dialog_key || '') ||
      state.description !== (comparisonBaseline.description || '') ||
      state.translatable !== (comparisonBaseline.data?.translatable !== false) ||
      state.speakerMode !== (comparisonBaseline.data?.speakerMode || 'none') ||
      state.speakerTypeId !== (comparisonBaseline.data?.speakerTypeId || null) ||
      JSON.stringify(state.speakerTemplateIds) !== JSON.stringify(comparisonBaseline.data?.speakerTemplateIds || []) ||
      JSON.stringify(state.dialogVariables) !== JSON.stringify(comparisonBaseline.data?.variables || []) ||
      JSON.stringify(sectionsToCheck) !== JSON.stringify(comparisonBaseline.data?.sections || []) ||
      flowPositionsChanged ||
      flowEdgesChanged;

    state.setHasUnsavedChanges(hasChanges);

    if (onUnsavedChangesChange) {
      // Pass discard handler to clear drafts when user discards changes
      const discardHandler = () => {
        clearDraft();
      };
      onUnsavedChangesChange(hasChanges, submitHandlers.handleSubmit, discardHandler);
    }

    // Save draft when state changes (for both new and existing dialogs)
    // Skip if we just saved successfully (to prevent saving stale draft)
    if (hasChanges && !skipDraftSaveRef.current) {
      // Defer the save to avoid state updates during render
      queueMicrotask(() => {
        saveDraftData({
          dialogName: state.dialogName,
          dialogKey: state.dialogKey,
          description: state.description,
          translatable: state.translatable,
          speakerMode: state.speakerMode,
          speakerTypeId: state.speakerTypeId,
          speakerTemplateIds: state.speakerTemplateIds,
          dialogVariables: state.dialogVariables,
          sections: state.sections,
          // Flow mode state
          viewMode: state.viewMode,
          localFlowSections: state.localFlowSections,
          flowNodes: state.flowNodes,
          flowEdges: state.flowEdges,
          flowViewport: state.flowViewport,
          flowInitialized: state.flowInitialized,
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.dialogName, state.dialogKey, state.description, state.translatable, state.speakerMode, state.speakerTypeId, state.speakerTemplateIds, state.dialogVariables, state.sections, state.localFlowSections, state.flowNodes, state.flowEdges, state.flowViewport, state.viewMode, state.flowInitialized, state.isDataLoaded, initialData, onUnsavedChangesChange, submitHandlers.handleSubmit, clearDraft, saveDraftData, isNewDialog]);

  // Reset snapshots only when unsaved changes becomes false (after save or revert)
  // We need a ref to track the previous hasUnsavedChanges state to detect the transition
  const prevHasUnsavedChangesRef = useRef(state.hasUnsavedChanges);

  useEffect(() => {
    // Only reset snapshots when hasUnsavedChanges transitions from true to false (save/revert completed)
    if (prevHasUnsavedChangesRef.current && !state.hasUnsavedChanges && state.flowNodes.length > 0) {
      flowPositionsSnapshotRef.current = getFlowPositionsSnapshot(state.flowNodes);
      flowEdgesSnapshotRef.current = getFlowEdgesSnapshot(state.flowEdges);

      // CRITICAL FIX: Update saved state snapshot with current state
      // This becomes the new baseline for change detection
      const sectionsToSnapshot = state.viewMode === 'flow' && state.flowInitialized
        ? state.localFlowSections
        : state.sections;

      savedStateSnapshotRef.current = {
        ...initialData,
        name: state.dialogName,
        dialog_key: state.dialogKey,
        description: state.description,
        data: {
          ...initialData?.data,
          sections: sectionsToSnapshot,
          translatable: state.translatable,
          speakerMode: state.speakerMode,
          speakerTypeId: state.speakerTypeId,
          speakerTemplateIds: state.speakerTemplateIds,
          variables: state.dialogVariables,
        },
      };


    }

    // Re-enable draft saving when user starts editing again (after a save)
    // This allows drafts to be saved for new changes after successful save
    if (!prevHasUnsavedChangesRef.current && state.hasUnsavedChanges) {
      skipDraftSaveRef.current = false;
    }

    prevHasUnsavedChangesRef.current = state.hasUnsavedChanges;
  }, [state.hasUnsavedChanges, state.flowNodes, state.flowEdges, state.dialogName, state.dialogKey, state.description, state.translatable, state.speakerMode, state.speakerTypeId, state.speakerTemplateIds, state.dialogVariables, state.viewMode, state.flowInitialized, state.localFlowSections, state.sections, initialData, getFlowPositionsSnapshot, getFlowEdgesSnapshot]);

  // Save draft when view mode changes (even if no other data changes)
  // This ensures the view mode preference is preserved when switching tabs
  useEffect(() => {
    if (!initialData?.id || isNewDialog) return; // Only for existing dialogs
    // Skip if we just saved successfully (to prevent saving stale draft)
    if (skipDraftSaveRef.current) return;

    // Defer the save to avoid state updates during render
    queueMicrotask(() => {
      saveDraftData({
        dialogName: state.dialogName,
        dialogKey: state.dialogKey,
        description: state.description,
        translatable: state.translatable,
        speakerMode: state.speakerMode,
        speakerTypeId: state.speakerTypeId,
        speakerTemplateIds: state.speakerTemplateIds,
        dialogVariables: state.dialogVariables,
        sections: state.sections,
        // Flow mode state
        viewMode: state.viewMode,
        localFlowSections: state.localFlowSections,
        flowNodes: state.flowNodes,
        flowEdges: state.flowEdges,
        flowViewport: state.flowViewport,
        flowInitialized: state.flowInitialized,
      });
    });
  }, [state.viewMode, state.flowViewport, initialData?.id, isNewDialog, saveDraftData, state.dialogName, state.dialogKey, state.description, state.translatable, state.speakerMode, state.speakerTypeId, state.speakerTemplateIds, state.dialogVariables, state.sections, state.localFlowSections, state.flowNodes, state.flowEdges, state.flowInitialized]);

  // Auto-generate dialog_key from dialogName
  useEffect(() => {
    if (!initialData?.id && state.dialogName) {
      const key = state.dialogName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

      if (key && key !== state.dialogKey) {
        state.setDialogKey(key);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.dialogName, state.dialogKey, initialData?.id]);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!inline && !isOpen) return null;

  const wizardContent = (
    <DialogNodeProvider>
      <div className={inline ? "relative w-full h-full flex flex-col" : ""} style={inline ? { pointerEvents: 'auto' } : undefined}>
      {/* Lock Status Banner */}
      {isLockedByOther && (
        <div className={inline ? "px-6 pt-4 max-w-6xl mx-auto w-full" : "px-6 pt-4"}>
          <LockStatusBanner
            lockStatus={lockStatus}
            isAcquiring={isAcquiringLock}
            lockError={lockError}
            canEdit={canEdit}
            onRefresh={refreshLockStatus}
            onForceTakeover={async () => {
              const success = await forceLock();
              if (success) {
                toast({
                  title: 'Edit access acquired',
                  description: 'You now have edit access to this dialog.',
                });
              }
            }}
            canForceTakeover={true}
            size="md"
          />
          <UpdateNotificationBanner
            hasPendingUpdate={hasPendingUpdate}
            updatedByName={updatedByName}
            updatedByAvatar={updatedByAvatar}
            updatedAt={updatedAt}
            onRefresh={async () => {
              // Refresh the dialog data
              if (initialData?.id && state.effectiveGameId) {
                const freshData = await fetchDialog(initialData.id, state.effectiveGameId);
                if (freshData) {
                  // Reset state with fresh data
                  state.setSections(freshData.data?.sections || []);
                  state.setDialogVariables(freshData.data?.variables || []);
                  state.setDialogName(freshData.name);
                  state.setDialogKey(freshData.dialog_key);
                  state.setDescription(freshData.description || '');
                  state.setTags(freshData.tags || []);
                  state.setStatus(freshData.status || 'published');
                  state.setTranslatable(freshData.data?.translatable !== false);
                  state.setSpeakerMode(freshData.data?.speakerMode || 'none');
                  state.setSpeakerTypeId(freshData.data?.speakerTypeId || null);
                  state.setSpeakerTemplateIds(freshData.data?.speakerTemplateIds || []);
                }
              }
              clearPendingUpdate();
            }}
            onDismiss={clearPendingUpdate}
            size="md"
          />
        </div>
      )}

      {/* Header */}
      <div className={inline ? "flex-shrink-0 pt-6 border-b border-grey-G2 bg-background" : "flex-shrink-0 pt-6 border-b border-grey-G2"}>
        <div className={inline ? "max-w-6xl mx-auto px-6 pb-6" : "pb-6"}>
          <UnifiedWizardHeader
            title={state.dialogName || 'New Dialog'}
            mode={initialData?.id ? 'edit' : 'create'}
            itemType="dialogs"
            itemId={initialData?.id}
            onTitleChange={state.setDialogName}
            onCancel={submitHandlers.handleCancel}
            onRevert={initialData?.id ? handleRevert : undefined}
            onSubmit={submitHandlers.handleSubmit}
            isSubmitting={state.isSubmitting}
            submitDisabled={!state.dialogName.trim() || (!inline && !state.hasUnsavedChanges && !!initialData?.id)}
            submitText={initialData?.id ? 'Update Dialog' : 'Create Dialog'}
            hasUnsavedChanges={state.hasUnsavedChanges}
            additionalBadges={
              state.dialogKey && (
                <span className="text-sm text-muted-foreground font-mono">
                  {state.dialogKey}
                </span>
              )
            }
          />
        </div>
      </div>

      {/* Content */}
      <div className={inline ? "flex-1 flex flex-col bg-background overflow-hidden" : "flex-1 flex flex-col overflow-hidden"}>
        {/* View Mode Selector - only show in normal flow for edit, write, and play modes */}
        {state.viewMode !== 'flow' && (
          <DialogViewModeSelector
            viewMode={state.viewMode}
            onViewModeChange={handleViewModeChange}
            inline={inline}
          />
        )}

        {state.viewMode === 'edit' ? (
          <div className="w-full h-full overflow-y-auto">
            <div className={inline ? "flex-1  max-w-6xl mx-auto px-6 w-full pb-96" : "flex-1 overflow-y-auto px-6"}>
              <div className="space-y-6 pb-4">
                {/* Basic Info */}
                <DialogBasicInfoForm
                  description={state.description}
                  translatable={state.translatable}
                  speakerMode={state.speakerMode}
                  speakerTypeId={state.speakerTypeId}
                  speakerTemplateIds={state.speakerTemplateIds}
                  gameId={state.effectiveGameId}
                  onDescriptionChange={state.setDescription}
                  onTranslatableChange={state.setTranslatable}
                  onSpeakerModeChange={state.setSpeakerMode}
                  onSpeakerTypeIdChange={state.setSpeakerTypeId}
                  onSpeakerTemplateIdsChange={state.setSpeakerTemplateIds}
                  t={t}
                />

                {/* Variable Declarations */}
                <DialogVariableDeclarations
                  variables={state.dialogVariables}
                  onUpdateVariables={state.setDialogVariables}
                />

                {/* Variables Panel */}
{/*                 <DialogVariablesPanel sections={state.sections} dialogVariables={state.dialogVariables} />
 */}
                {/* Sections */}
                <DialogSectionsList
                  key={`sections-v${state.sectionsVersion}`}
                  sections={state.sections}
                  sectionIdentifiers={state.sectionIdentifiers}
                  currentDialogId={initialData?.id}
                  dialogTranslatable={state.translatable}
                  speakerMode={state.speakerMode}
                  speakerTypeId={state.speakerTypeId}
                  speakerTemplateIds={state.speakerTemplateIds}
                  dialogVariables={state.dialogVariables}
                  currentSectionIndex={state.currentSectionIndex}
                  onSectionClick={state.setCurrentSectionIndex}
                  onAddSection={sectionHandlers.addSection}
                  onUpdateSection={sectionHandlers.updateSection}
                  onRemoveSection={sectionHandlers.removeSection}
                  onAddField={sectionHandlers.addField}
                  onUpdateField={sectionHandlers.updateField}
                  onRemoveField={sectionHandlers.removeField}
                  onAddOption={sectionHandlers.addOption}
                  onUpdateOption={sectionHandlers.updateOption}
                  onRemoveOption={sectionHandlers.removeOption}
                  onAddConditionOption={sectionHandlers.addConditionOption}
                  onUpdateConditionOption={sectionHandlers.updateConditionOption}
                  onRemoveConditionOption={sectionHandlers.removeConditionOption}
                  onSectionDragEnd={sectionHandlers.handleSectionDragEnd}
                  gameId={state.effectiveGameId}
                  t={t}
                />
              </div>
            </div>
          </div>
        ) : state.viewMode === 'write' ? (
          <div className="flex-1 overflow-hidden">
            <DialogWriteView
              sections={state.sections}
              speakerMode={state.speakerMode}
              speakerTypeId={state.speakerTypeId}
              speakerTemplateIds={state.speakerTemplateIds}
              gameId={state.effectiveGameId}
              onUpdateField={sectionHandlers.updateField}
              onUpdateOption={sectionHandlers.updateOption}
              onUpdateSection={sectionHandlers.updateSection}
            />
          </div>
        ) : state.viewMode === 'flow' ? (
          <DialogFlowView
            inline={inline}
            sections={state.sections}
            localFlowSections={state.localFlowSections}
            flowId={state.flowId}
            flowNodes={state.flowNodes}
            flowEdges={state.flowEdges}
            flowViewport={state.flowViewport}
            effectiveGameId={state.effectiveGameId}
            initialData={initialData}
            dialogName={state.dialogName}
            dialogKey={state.dialogKey}
            description={state.description}
            translatable={state.translatable}
            speakerMode={state.speakerMode}
            speakerTypeId={state.speakerTypeId}
            speakerTemplateIds={state.speakerTemplateIds}
            dialogVariables={state.dialogVariables}
            contextMenu={state.contextMenu}
            viewMode={state.viewMode}
            onViewModeChange={handleViewModeChange}
            onConnect={flowHandlers.handleConnect}
            onConnectEnd={flowHandlers.handleConnectionEnd}
            onPaneContextMenu={flowHandlers.handlePaneContextMenu}
            onNodesChange={flowHandlers.handleNodesChange}
            onEdgesChange={flowHandlers.handleEdgesChange}
            onViewportChange={state.setFlowViewport}
            onDrop={flowHandlers.handleDrop}
            onDragOver={flowHandlers.handleDragOver}
            onNodeClick={(node) => {
              // Extract section index from node data
              const sectionIndex = node.data?.sectionIndex;
              if (typeof sectionIndex === 'number') {
                state.setCurrentSectionIndex(sectionIndex);
              }
              
              // Update selected node context for node-info sidebar
              if (dialogNodeContext) {
                const nodeData = node.data?.node as DialogNode | undefined;
                const nodeIndex = (node.data?.nodeIndex ?? node.data?.sectionIndex) as number | null;
                if (nodeData) {
                  dialogNodeContext.setSelectedNode(node.id, nodeData, nodeIndex);
                }
              }

              // Notify sidebar to switch to dialog section tab when node is clicked in flow mode
              if (state.viewMode === 'flow') {
                window.dispatchEvent(new CustomEvent('dialog-node-clicked-in-flow'));
              }
            }}
            onCreateFieldFromConnection={flowHandlers.handleCreateFieldFromConnection}
            onContextMenuClose={() => state.setContextMenu({ show: false, position: { x: 0, y: 0 } })}
            addCallbackToNodes={flowHandlers.addCallbackToNodes}
          />
        ) : (
          <div className="flex-1 overflow-hidden">
            <DialogPlayView
              sections={state.sections}
              currentSectionIndex={state.currentSectionIndex}
              speakerMode={state.speakerMode}
              speakerTypeId={state.speakerTypeId}
              speakerTemplateIds={state.speakerTemplateIds}
              dialogVariables={state.dialogVariables}
              gameId={state.effectiveGameId}
              onSectionChange={state.setCurrentSectionIndex}
              onUpdateField={sectionHandlers.updateField}
              onDialogSwitch={async (dialogId: string, sectionIdentifier?: string | null) => {
                try {
                  const targetDialog = await fetchDialog(dialogId, state.effectiveGameId);

                  let newSections = ensureSectionIds(targetDialog.data?.sections || []);
                  newSections = migrateTextToDialog(newSections);
                  newSections = migrateEndTextToEndDialog(newSections);

                  state.setDialogName(targetDialog.name || 'Dialog');
                  state.setDialogKey(targetDialog.dialog_key || '');
                  state.setDescription(targetDialog.description || '');
                  state.setTranslatable(targetDialog.data?.translatable !== false);
                  state.setSpeakerMode(targetDialog.data?.speakerMode || 'none');
                  state.setSpeakerTypeId(targetDialog.data?.speakerTypeId || null);
                  state.setSpeakerTemplateIds(targetDialog.data?.speakerTemplateIds || []);
                  state.setSections(newSections);

                  let targetSectionIndex = 0;
                  if (sectionIdentifier) {
                    const foundIndex = newSections.findIndex(s => s.section_identifier === sectionIdentifier);
                    if (foundIndex !== -1) {
                      targetSectionIndex = foundIndex;
                    }
                  }

                  state.setCurrentSectionIndex(targetSectionIndex);
                } catch (error) {
                  console.error('Failed to load dialog:', error);
                  throw error;
                }
              }}
            />
          </div>
        )}
      </div>
      </div>
    </DialogNodeProvider>
  );

  // Inline mode: render directly without Dialog wrapper
  if (inline) {
    return wizardContent;
  }

  // Modal mode: wrap in Dialog
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogTitle className="sr-only">
          {initialData?.id ? 'Edit Dialog' : 'Create Dialog'}
        </DialogTitle>
        {wizardContent}
      </DialogContent>
    </Dialog>
  );
}
